import {
  alpha,
  Box,
  Card,
  CardContent,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo } from "react";
import {
  ActivityCounts,
  CategoryBreakdown,
  SalesOverviewStats,
  SalesTeamSummary,
  SegmentBreakdown,
  SegmentKey,
} from "../../../store/services/salesManagerService";
import {
  CATEGORY_LABELS,
  formatPercent,
  getCompletionRateColor,
  getSubtypeCounts,
  SEGMENT_LABELS,
  SEGMENT_ORDER,
  SUBTYPE_LABELS,
  SUBTYPE_ORDER,
} from "../shared/breakdown";

interface SegmentMatrixTabProps {
  overview: SalesOverviewStats | undefined;
  teamSummary: SalesTeamSummary | undefined;
  isLoading: boolean;
}

type CategoryKey = "Communication" | "Followup" | "Total";

interface SegmentRowConfig {
  key: SegmentKey | "Total";
  label: string;
  emphasis: boolean; // "Total"-segment får tyngre styling
}

const CATEGORY_ROWS: {
  key: CategoryKey;
  label: string;
  emphasis: boolean;
}[] = [
  { key: "Communication", label: CATEGORY_LABELS.Communication, emphasis: false },
  { key: "Followup", label: CATEGORY_LABELS.Followup, emphasis: false },
  { key: "Total", label: CATEGORY_LABELS.Total, emphasis: true },
];

const MatrixCell: React.FC<{
  counts: ActivityCounts;
  emphasis?: boolean;
}> = ({ counts, emphasis }) => {
  const theme = useTheme();
  const rateColor = getCompletionRateColor(counts.CompletionRate, theme);
  const bg =
    counts.Total === 0
      ? "transparent"
      : alpha(rateColor, emphasis ? 0.16 : 0.09);

  if (counts.Total === 0) {
    return (
      <TableCell
        align="right"
        sx={{
          bgcolor: bg,
          borderLeft: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          fontWeight: emphasis ? 700 : 400,
        }}
      >
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      </TableCell>
    );
  }

  return (
    <Tooltip
      title={
        <Box sx={{ fontSize: 12 }}>
          <div>Totalt: {counts.Total}</div>
          <div>Slutfört: {counts.Completed}</div>
          <div>Aktivt: {counts.Active}</div>
          <div>Overdue: {counts.Overdue}</div>
          <div>Slutförandegrad: {formatPercent(counts.CompletionRate)}</div>
        </Box>
      }
      arrow
      placement="top"
    >
      <TableCell
        align="right"
        sx={{
          bgcolor: bg,
          borderLeft: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          cursor: "help",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: emphasis ? 700 : 500, lineHeight: 1.2 }}
        >
          {counts.Completed} / {counts.Total}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: rateColor,
            fontWeight: 600,
          }}
        >
          {formatPercent(counts.CompletionRate)}
        </Typography>
      </TableCell>
    </Tooltip>
  );
};

const SegmentMatrixTab: React.FC<SegmentMatrixTabProps> = ({
  overview,
  teamSummary,
  isLoading,
}) => {
  const theme = useTheme();

  // Primär källa är teamSummary; faller tillbaka på overview.
  const breakdown: SegmentBreakdown | undefined =
    teamSummary?.Breakdown ?? overview?.Breakdown;

  // Visa bara segment som har minst en aktivitet — matrisen blir lugnare
  // att läsa när tomma buckets (t.ex. NetworkOnly, Unknown) inte skräpar.
  const segmentRows: SegmentRowConfig[] = useMemo(() => {
    if (!breakdown) return [];
    const rows: SegmentRowConfig[] = SEGMENT_ORDER.filter(
      (key) => breakdown[key].Total.Total.Total > 0
    ).map((key) => ({
      key,
      label: SEGMENT_LABELS[key],
      emphasis: false,
    }));
    rows.push({
      key: "Total",
      label: SEGMENT_LABELS.Total,
      emphasis: true,
    });
    return rows;
  }, [breakdown]);

  if (isLoading || !breakdown) {
    return (
      <Skeleton variant="rectangular" height={480} sx={{ borderRadius: 3 }} />
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: `0 2px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}
    >
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            Segment × Aktivitetstyp-matris
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Slutförda / totalt per kundsegment, kategori och aktivitetstyp.
            Färg reflekterar slutförandegrad. Hovra över en cell för fler
            detaljer.
          </Typography>
        </Box>

        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2 }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  "& th": { fontWeight: 700 },
                }}
              >
                <TableCell sx={{ minWidth: 160 }}>Segment</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Kategori</TableCell>
                {SUBTYPE_ORDER.map((subtype) => (
                  <TableCell key={subtype} align="right">
                    {SUBTYPE_LABELS[subtype]}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    borderLeft: `2px solid ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                  }}
                >
                  Totalt
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {segmentRows.flatMap((segmentRow) => {
                const segmentBreakdown: CategoryBreakdown =
                  breakdown[segmentRow.key];

                return CATEGORY_ROWS.map((catRow, catIndex) => {
                  const isLastInSegment = catIndex === CATEGORY_ROWS.length - 1;
                  const isOverallTotal =
                    segmentRow.emphasis && catRow.emphasis;

                  const subtypeBreakdown = segmentBreakdown[catRow.key];

                  return (
                    <TableRow
                      key={`${segmentRow.key}-${catRow.key}`}
                      sx={{
                        bgcolor: isOverallTotal
                          ? alpha(theme.palette.primary.main, 0.06)
                          : segmentRow.emphasis
                          ? alpha(theme.palette.primary.main, 0.02)
                          : undefined,
                        borderBottom: isLastInSegment
                          ? `2px solid ${alpha(
                              theme.palette.divider,
                              0.35
                            )}`
                          : undefined,
                        "& td, & th": {
                          fontWeight: catRow.emphasis ? 700 : 400,
                        },
                      }}
                    >
                      {catIndex === 0 ? (
                        <TableCell
                          rowSpan={CATEGORY_ROWS.length}
                          sx={{
                            fontWeight: 700,
                            verticalAlign: "top",
                            borderRight: `1px solid ${alpha(
                              theme.palette.divider,
                              0.12
                            )}`,
                            bgcolor: segmentRow.emphasis
                              ? alpha(theme.palette.primary.main, 0.04)
                              : undefined,
                          }}
                        >
                          <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight="bold">
                              {segmentRow.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {segmentBreakdown.Total.Total.Total} aktiviteter
                            </Typography>
                          </Stack>
                        </TableCell>
                      ) : null}
                      <TableCell sx={{ fontWeight: catRow.emphasis ? 700 : 500 }}>
                        {catRow.label}
                      </TableCell>
                      {SUBTYPE_ORDER.map((subtype) => (
                        <MatrixCell
                          key={subtype}
                          counts={getSubtypeCounts(subtypeBreakdown, subtype)}
                          emphasis={catRow.emphasis || segmentRow.emphasis}
                        />
                      ))}
                      <TableCell
                        align="right"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderLeft: `2px solid ${alpha(
                            theme.palette.primary.main,
                            0.2
                          )}`,
                          fontWeight: 700,
                        }}
                      >
                        <MatrixCellInline
                          counts={subtypeBreakdown.Total}
                          emphasis
                        />
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

// Inline-variant utan TableCell-wrapper (används i Totalt-kolumnen)
const MatrixCellInline: React.FC<{
  counts: ActivityCounts;
  emphasis?: boolean;
}> = ({ counts, emphasis }) => {
  const theme = useTheme();
  const rateColor = getCompletionRateColor(counts.CompletionRate, theme);
  if (counts.Total === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  return (
    <Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: emphasis ? 700 : 500, lineHeight: 1.2 }}
      >
        {counts.Completed} / {counts.Total}
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: rateColor, fontWeight: 600 }}
      >
        {formatPercent(counts.CompletionRate)}
      </Typography>
    </Box>
  );
};

export default SegmentMatrixTab;
